import { useMemo } from "react";
import { Box, Divider } from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import { amber, blue, green, grey, red } from "@mui/material/colors";
import isNil from "lodash/isNil";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { LIQUIDITY_SOURCE_TYPES, getSourceMeta } from "./constants";

const LiquiditySummaryColumn = ({
  totals,
  requiredTotal,
  coverage,
  sources,
}) => {
  const shortfall = useMemo(() => {
    if (!requiredTotal) return null;
    const diff = totals.validated - requiredTotal;
    return { amount: diff, isShort: diff < 0 };
  }, [totals.validated, requiredTotal]);

  const hasDiscount = useMemo(
    () => totals.validated !== totals.gross,
    [totals.validated, totals.gross],
  );

  return (
    <Flx column gap={2}>
      <Card>
        <SectionHeader
          title="Total liquidity"
          subtitle={hasDiscount ? "After source discounts" : null}
        />
        <Flx column g={0.5} sx={{ mb: 2 }}>
          <Txt
            sx={{
              fontSize: 28,
              fontWeight: 700,
              color: "#0f172a",
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.1,
            }}
          >
            {formatDollar(totals.validated)}
          </Txt>
          {hasDiscount && (
            <Txt sx={{ fontSize: 11, color: grey[500] }}>
              from {formatDollar(totals.gross)} gross
            </Txt>
          )}
        </Flx>

        <Divider sx={{ mb: 1.5 }} />

        <Flx column g={1}>
          {Object.values(LIQUIDITY_SOURCE_TYPES).map((sourceType) => {
            const meta = getSourceMeta(sourceType);
            const gross = totals.byTypeGross?.[sourceType] || 0;
            const validated = totals.byTypeValidated?.[sourceType] || 0;
            const hasAny = gross > 0;
            if (!hasAny) return null;
            const Icon = meta.Icon;
            return (
              <Flx key={sourceType} ac jb g={1}>
                <Flx ac g={1} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: 0.75,
                      background: meta.iconBg,
                      color: meta.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      "& svg": { fontSize: 13 },
                    }}
                  >
                    <Icon />
                  </Box>
                  <Txt
                    sx={{
                      fontSize: 12,
                      color: grey[700],
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {meta.shortLabel}
                  </Txt>
                </Flx>
                <Flx column g={0} sx={{ alignItems: "flex-end" }}>
                  <Txt
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: "#0f172a",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatDollar(validated)}
                  </Txt>
                  {validated !== gross && (
                    <Txt
                      sx={{
                        fontSize: 10,
                        color: grey[500],
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatDollar(gross)} gross
                    </Txt>
                  )}
                </Flx>
              </Flx>
            );
          })}
        </Flx>
      </Card>

      <VerifiedLiquidityCard totals={totals} />

      <CoverageCard
        coverage={coverage}
        requiredTotal={requiredTotal}
        shortfall={shortfall}
        validated={totals.validated}
      />

      {totals.countMissingDoc > 0 && (
        <Card sx={{ background: amber[50], borderColor: amber[200] }}>
          <Flx ac g={1} sx={{ mb: 0.5 }}>
            <ErrorOutline sx={{ fontSize: 18, color: amber[800] }} />
            <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: amber[900] }}>
              Missing source documents
            </Txt>
          </Flx>
          <Txt sx={{ fontSize: 11.5, color: amber[900], mb: 0.5 }}>
            {totals.countMissingDoc} of {totals.countRequiringDoc} sources need
            a sourcing document attached.
          </Txt>
        </Card>
      )}
    </Flx>
  );
};

const VerifiedLiquidityCard = ({ totals }) => {
  // Filter+sum locally from sources where verified === true. Reads
  // totals.verified, which computeTotals derives by summing validatedAmount
  // across rows with verified=true — keeps the UI in lockstep with whatever
  // the user has just toggled, no waiting on the loan-doc rollup.
  const value = Number(totals?.verified || 0);
  const verifiedCount = totals?.countVerified || 0;
  const totalCount = totals?.count || 0;
  const allVerified = totalCount > 0 && verifiedCount === totalCount;
  const accent = allVerified ? green[600] : blue[600];

  return (
    <Card>
      <SectionHeader
        title="Verified liquidity"
        subtitle="Sum of validated amounts on rows marked verified"
      />
      <Flx ac g={1} sx={{ mb: 3 }}>
        <VerifiedOutlined sx={{ fontSize: 26, color: accent }} />
        <Txt
          sx={{
            fontSize: 24,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatDollar(value)}
        </Txt>
      </Flx>
      <Flx column g={0.5} sx={{ mt: 2 }}>
        <KeyValue
          label="Verified rows"
          value={totalCount === 0 ? "—" : `${verifiedCount} of ${totalCount}`}
          valueColor={allVerified ? green[700] : grey[800]}
        />
        <KeyValue
          label="All rows (validated)"
          value={formatDollar(totals?.validated || 0)}
        />
      </Flx>
    </Card>
  );
};

const CoverageCard = ({ coverage, requiredTotal, shortfall, validated }) => {
  if (isNil(requiredTotal) || requiredTotal === 0) {
    return (
      <Card>
        <SectionHeader
          title="Coverage"
          subtitle="Fill in required liquidity to calculate"
        />
        <Flx ac g={1}>
          <InfoOutlined sx={{ fontSize: 18, color: grey[400] }} />
          <Txt sx={{ fontSize: 12, color: grey[500] }}>
            No required amount set
          </Txt>
        </Flx>
      </Card>
    );
  }

  const meets = coverage >= 1;
  const color = meets ? green[700] : red[600];
  const progressColor = meets
    ? green[500]
    : coverage >= 0.75
      ? amber[600]
      : red[500];

  // Display: under 100% shows percent ("78%"), at/over 100% shows multiplier
  // ("5.5×"). Multiplier reads more honestly than 547% — your eye can size up
  // a 5.5× cushion intuitively, where 547% just feels arbitrary.
  const displayLabel = meets
    ? `${coverage.toFixed(coverage >= 10 ? 0 : 1)}×`
    : `${Math.round(coverage * 100)}%`;
  const displaySubtitle = meets ? "over required" : "of required";

  return (
    <Card>
      <SectionHeader title="Coverage" />

      <Flx ac g={1.25} sx={{ mb: 0.5 }}>
        {meets ? (
          <CheckCircleOutline sx={{ fontSize: 24, color }} />
        ) : (
          <ErrorOutline sx={{ fontSize: 24, color }} />
        )}
        <Flx ac g={0.75} sx={{ alignItems: "baseline" }}>
          <Txt
            sx={{
              fontSize: 26,
              fontWeight: 700,
              color,
              lineHeight: 1.1,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {displayLabel}
          </Txt>
          <Txt sx={{ fontSize: 11.5, color: grey[600] }}>{displaySubtitle}</Txt>
        </Flx>
      </Flx>

      {/* Threshold-marked bar: total fill represents validated, the tick at
          18% (or wherever required falls) marks the minimum. Anything past
          the tick is surplus. Replaces a maxed-out 100% bar that looked
          identical for any ratio above 1×. */}
      <CoverageBar
        coverage={coverage}
        meets={meets}
        progressColor={progressColor}
      />

      <Flx column g={0.5} sx={{ mt: 1.5 }}>
        <KeyValue label="Required" value={formatDollar(requiredTotal)} />
        <Divider sx={{ my: 0.5 }} />
        <KeyValue
          label={shortfall?.isShort ? "Shortfall" : "Surplus"}
          value={formatDollar(Math.abs(shortfall?.amount || 0))}
          valueColor={shortfall?.isShort ? red[600] : green[700]}
          bold
        />
      </Flx>
    </Card>
  );
};

const CoverageBar = ({ coverage, meets, progressColor }) => {
  // When meets: bar fills to 100%, tick sits at the position representing
  // 1× (i.e. required). Under-100% means the surplus zone is the visible bar
  // length and the threshold is at 100%, but to keep the math simple we show
  // the tick at min(100%, requiredPosition) and let the fill represent
  // proportional progress to required.
  const tickPercent = meets
    ? Math.max(8, Math.min(95, (1 / coverage) * 100))
    : 100;
  const fillPercent = meets ? 100 : Math.max(2, coverage * 100);

  return (
    <Box
      sx={{
        position: "relative",
        height: 8,
        borderRadius: 4,
        background: grey[100],
        overflow: "visible",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: `${fillPercent}%`,
          background: progressColor,
          borderRadius: 4,
        }}
      />
      {/* threshold tick */}
      <Box
        sx={{
          position: "absolute",
          left: `${tickPercent}%`,
          top: -3,
          bottom: -3,
          width: 2,
          background: grey[700],
          transform: "translateX(-1px)",
        }}
      />
    </Box>
  );
};

const KeyValue = ({ label, value, valueColor, bold }) => (
  <Flx jb ac>
    <Txt sx={{ fontSize: 11.5, color: grey[600] }}>{label}</Txt>
    <Txt
      sx={{
        fontSize: 12.5,
        fontWeight: bold ? 700 : 500,
        color: valueColor || "#0f172a",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Txt>
  </Flx>
);

const SectionHeader = ({ title, subtitle }) => (
  <Flx column g={0.25} sx={{ mb: 1.5 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: grey[600],
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {title}
    </Txt>
    {subtitle && <Txt sx={{ fontSize: 11, color: grey[500] }}>{subtitle}</Txt>}
  </Flx>
);

const Card = ({ children, sx }) => (
  <Box
    sx={{
      background: "#fff",
      border: `1px solid ${grey[200]}`,
      borderRadius: 2,
      p: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

export default LiquiditySummaryColumn;
